'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { Header } from '@/components/Header';
import { StatsGrid } from '@/components/StatsGrid';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  User,
  Calendar,
  Mail,
  Edit,
  Share2,
  TrendingUp,
  Award,
  Flame,
} from 'lucide-react';
import {
  calculateWatchStats,
  calculateGenreDistribution,
  calculateStreaks,
  getMemberDuration,
} from '@/lib/profileUtils';

export default function ProfilePage() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const router = useRouter();
  const { watchlist } = useStore();
  const [stats, setStats] = useState<ReturnType<typeof calculateWatchStats> | null>(null);
  const [genres, setGenres] = useState<ReturnType<typeof calculateGenreDistribution>>([]);
  const [streaks, setStreaks] = useState({ currentStreak: 0, longestStreak: 0 });

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/auth/signin');
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (watchlist.length > 0) {
      setStats(calculateWatchStats(watchlist));
      setGenres(calculateGenreDistribution(watchlist, 5));
      setStreaks(calculateStreaks(watchlist));
    }
  }, [watchlist]);

  if (authLoading || !user || !userProfile) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  // Get join date from userProfile.createdAt or Firebase user metadata
  const joinDate = userProfile.createdAt || user.metadata?.creationTime;
  const memberSince = joinDate ? getMemberDuration(joinDate) : 'Unknown';

  return (
    <div className="min-h-screen bg-black">
      <Header showSearch={true} transparent={true} page="dashboard" />

      <main className="pt-20 md:pt-24 pb-12">
        {/* Profile Header */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border-b border-white/10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40"></div>

          <div className="relative z-10 container mx-auto px-4 pt-16 pb-12">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Avatar */}
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center ring-8 ring-red-500/20 flex-shrink-0 overflow-hidden">
                {userProfile.photoURL || user.photoURL ? (
                  <img
                    src={userProfile.photoURL || user.photoURL || ''}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-5xl font-bold text-white">
                    {(userProfile.username || user.email)?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left space-y-4">
                <div>
                  <div className="flex items-center justify-center md:justify-start gap-3 flex-wrap">
                    <h1 className="text-4xl font-bold text-white">
                      {userProfile.displayName || userProfile.username || user.email?.split('@')[0]}
                    </h1>
                    <Badge
                      className={
                        userProfile.profileVisibility === 'public'
                          ? 'bg-green-600/20 border-2 border-green-500/80 text-green-400 font-bold px-3 py-1'
                          : userProfile.profileVisibility === 'friends'
                          ? 'bg-blue-600/20 border-2 border-blue-500/80 text-blue-400 font-bold px-3 py-1'
                          : 'bg-gray-600/20 border-2 border-gray-500/80 text-gray-400 font-bold px-3 py-1'
                      }
                    >
                      {userProfile.profileVisibility?.toUpperCase() || 'PRIVATE'}
                    </Badge>
                  </div>

                  {userProfile.bio && (
                    <p className="text-lg text-gray-300 mt-2 max-w-2xl">
                      {userProfile.bio}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center md:justify-start gap-6 text-sm text-gray-400 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {userProfile.showEmail ? user.email : 'Hidden'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Member for {memberSince}
                  </div>
                  {userProfile.discordUsername && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {userProfile.discordUsername}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center md:justify-start gap-3 pt-2">
                  <Button
                    onClick={() => router.push('/settings')}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                  {userProfile.profileVisibility === 'public' && (
                    <Button variant="outline" className="border-gray-600 text-gray-300">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Streaks Section */}
          {(streaks.currentStreak > 0 || streaks.longestStreak > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-orange-900/20 to-red-900/20 border-orange-600/30 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-orange-600/20 flex items-center justify-center">
                    <Flame className="h-8 w-8 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Current Streak</p>
                    <p className="text-4xl font-bold text-white">{streaks.currentStreak}</p>
                    <p className="text-xs text-gray-500">days in a row</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-600/30 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-600/20 flex items-center justify-center">
                    <Award className="h-8 w-8 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Longest Streak</p>
                    <p className="text-4xl font-bold text-white">{streaks.longestStreak}</p>
                    <p className="text-xs text-gray-500">days total</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Stats Grid */}
          {stats && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-red-500" />
                Your Statistics
              </h2>
              <StatsGrid stats={stats} />
            </div>
          )}

          {/* Top Genres */}
          {genres.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Top Genres</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {genres.map((genre, index) => (
                  <Card
                    key={genre.genreId}
                    className="bg-gray-900/50 border-gray-800 p-6"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-white">{genre.genreName}</h3>
                      <Badge className="bg-red-600 text-white">#{index + 1}</Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">{genre.count} items</span>
                        <span className="text-white font-semibold">{genre.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-red-600 to-red-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${genre.percentage}%` }}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {watchlist.length === 0 && (
            <Card className="bg-gray-900/50 border-gray-800 p-12">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-gray-800 flex items-center justify-center">
                  <TrendingUp className="h-10 w-10 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">No Stats Yet</h3>
                  <p className="text-gray-400">
                    Start adding shows and movies to your watchlist to see your statistics!
                  </p>
                </div>
                <Button
                  onClick={() => router.push('/dashboard')}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Go to Dashboard
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
